import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import {
  BUY, SELL, SectionContainer, SectionHeader, SectionWrapper,
  UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from 'src/enosikit/components/Chart/components/chartConstants';
import { getDirectionHeaderLabel } from 'src/enosikit/components/Chart/helpers/cards';
import { getTooltipFormatFunc } from 'src/enosikit/components/Chart/helpers/tooltip';
import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE,
} from 'src/util/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';
import getMeterLabel from 'src/util/meter';

/**
 * Renders one Property meter tooltip section, including its value and status flags.
 * @param {object} props Component props.
 * @param {BUY|SELL} props.direction Meter data direction.
 * @param {object} props.tooltipData Meter tooltip datum.
 * @param {Function} props.tooltipFormatFunc Value formatter.
 * @param {string} props.unitLabel Unit display label.
 * @returns {React.ReactElement} Meter tooltip section.
 */
function MeterTooltipSection({
  direction, tooltipData = null, tooltipFormatFunc, unitLabel,
}) {
  const intl = useIntl();
  const {
    flags, value, title, identifier,
  } = tooltipData || {};
  const directionHeader = getDirectionHeaderLabel(intl, direction);
  const meterLabel = getMeterLabel(identifier, title);

  return (
    <SectionWrapper>
      <SectionHeader>
        {meterLabel ? `${directionHeader} - ${meterLabel}` : directionHeader}
      </SectionHeader>
      <div>
        {tooltipFormatFunc(value, unitLabel)}
      </div>
      {flags?.length > 0 && (
        <SectionContainer>
          {flags.map((flag) => flag.identifier).join(', ')}
        </SectionContainer>
      )}
    </SectionWrapper>
  );
}

MeterTooltipSection.propTypes = {
  direction: PropTypes.oneOf([BUY, SELL]).isRequired,
  tooltipData: PropTypes.shape({}),
  tooltipFormatFunc: PropTypes.func.isRequired,
  unitLabel: PropTypes.string.isRequired,
};

/**
 * Renders one direction of Property meter data as either a summary or a meter collection.
 * @param {object} props Component props.
 * @param {boolean} props.aggByMeter Whether tooltip data is keyed by meter ID.
 * @param {BUY|SELL} props.direction Meter data direction.
 * @param {object} props.tooltipData Meter tooltip data.
 * @param {Function} props.tooltipFormatFunc Value formatter.
 * @param {string} props.unitLabel Unit display label.
 * @returns {React.ReactElement|null} Direction tooltip content.
 */
function MeterTooltipDirectionGroup({
  aggByMeter, direction, tooltipData = null, tooltipFormatFunc, unitLabel,
}) {
  if (!tooltipData) {
    return null;
  }

  if (!aggByMeter) {
    return (
      <MeterTooltipSection
        direction={direction}
        tooltipData={tooltipData}
        tooltipFormatFunc={tooltipFormatFunc}
        unitLabel={unitLabel}
      />
    );
  }

  const sortedMeterIds = Object.keys(tooltipData)
    .sort((a, b) => (tooltipData[b]?.value ?? 0) - (tooltipData[a]?.value ?? 0));

  return (
    <>
      {sortedMeterIds.map((meterId) => (
        <MeterTooltipSection
          key={meterId}
          direction={direction}
          tooltipData={tooltipData[meterId]}
          tooltipFormatFunc={tooltipFormatFunc}
          unitLabel={unitLabel}
        />
      ))}
    </>
  );
}

MeterTooltipDirectionGroup.propTypes = {
  aggByMeter: PropTypes.bool.isRequired,
  direction: PropTypes.oneOf([BUY, SELL]).isRequired,
  tooltipData: PropTypes.shape({}),
  tooltipFormatFunc: PropTypes.func.isRequired,
  unitLabel: PropTypes.string.isRequired,
};

/**
 * Renders the Property meter chart tooltip for consumed and generated data.
 * @param {object} props Component props.
 * @param {object} props.chartView Property chart aggregation and grouping options.
 * @param {object} props.tooltipData Consumed and generated tooltip data.
 * @param {string} props.unit Chart unit.
 * @param {string} props.unitLabel Unit display label.
 * @returns {React.ReactElement|null} Property meter tooltip content.
 */
export default function MeterChartTooltip({
  chartView = null, tooltipData = null, unit = null, unitLabel = null,
}) {
  const intlCurrency = useIntlCurrency();

  if (!tooltipData || !chartView) {
    return null;
  }

  const { tooltipConsumed, tooltipGenerated } = tooltipData;
  if (!tooltipConsumed && !tooltipGenerated) {
    return null;
  }

  const aggByMeter = chartView.aggregateBy === DATA_AGGREGATE_BY_METER;
  const tooltipFormatFunc = getTooltipFormatFunc(unit, undefined, intlCurrency);

  return (
    <>
      <MeterTooltipDirectionGroup
        aggByMeter={aggByMeter}
        direction={BUY}
        tooltipData={tooltipConsumed}
        tooltipFormatFunc={tooltipFormatFunc}
        unitLabel={unitLabel}
      />
      <MeterTooltipDirectionGroup
        aggByMeter={aggByMeter}
        direction={SELL}
        tooltipData={tooltipGenerated}
        tooltipFormatFunc={tooltipFormatFunc}
        unitLabel={unitLabel}
      />
    </>
  );
}

MeterChartTooltip.propTypes = {
  chartView: PropTypes.shape({
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_METER,
      DATA_AGGREGATE_BY_PROPERTY,
    ]),
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]),
  }),
  tooltipData: PropTypes.shape({
    tooltipConsumed: PropTypes.shape({}),
    tooltipGenerated: PropTypes.shape({}),
  }),
  unit: PropTypes.oneOf([UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY]),
  unitLabel: PropTypes.string,
};
